import { ItemCard, ItemCardContainer } from "@/components/ItemCard";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { getOrderImage } from "@/utils/getItemImage";
import { useOrder } from "@repo/api";
import { Skeleton, crossIcon, pencilIcon, shoppingBagIcon } from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

import { NotFoundOrderItem } from "../components/NotFoundOrderItem";
import { OrderCardDetails } from "../components/OrderCardDetails";
import { onDeleteOrder } from "../utils";
import { isOrderAddedToProjectValue } from "./utils";

interface OrderItemProps {
  opportunityId?: number;
  orderId: string;
  editable?: boolean;
}

export const OrderItem = ({
  opportunityId,
  orderId,
  editable,
}: OrderItemProps) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const { opportunity } = useActiveProject();
  const { market, language, general } = useSettings();
  const { data: order, isValidating } = useOrder(orderId);
  const isWaitingList =
    opportunity?.orders?.allOrders?.find((o) => o.orderId === orderId)
      ?.waitingListOrder ?? false;

  const openOrderInSalja = {
    onAddToBag: (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      const link = `${
        import.meta.env.VITE__WEB_SALJA_V2
      }/${market}/${language}/orders/view/order/${order?.orderNo}`;

      window.open(link, "_blank");
    },
    icon: shoppingBagIcon,
    disabled: !order?.orderNo,
    loading: false,
  };

  const openOrderHistory = () => {
    if (window.salja?.orderHistory?.open && order) {
      window.salja.orderHistory.open(order.orderNo);
    }
  };

  const onRemoveFromProject = () => {
    if (!order) {
      return;
    }
    pushModal({
      type: "CONFIRM",

      onConfirm: () =>
        onDeleteOrder({
          opportunityId,
          orderId: order.orderNo,
          updateToast,
        }),
      title: t("action.remove_from_project"),
      message: t("prompt.confirm_remove_order"),
    });
  };

  const menuItems = [
    {
      icon: crossIcon,
      title: t("action.remove_from_project"),
      onClick: () => {
        onRemoveFromProject();
        trackAnalytics("remove_order_from_project");
      },
    },
    {
      icon: pencilIcon,
      title: t("action.order_history"),
      onClick: () => {
        openOrderHistory();
        trackAnalytics("open_order_history");
      },
    },
  ];

  if (isValidating && !order) {
    return (
      <ItemCardContainer>
        <Skeleton height="115px" width="100%" />
      </ItemCardContainer>
    );
  }

  if (!order?.orderNo) {
    return (
      <NotFoundOrderItem
        key={orderId}
        orderId={orderId}
        opportunityId={opportunityId}
      />
    );
  }

  return (
    <ItemCard
      editable={editable}
      onClick={() => {
        if (editable) {
          pushModal({ type: "ORDER", orderId: order.orderNo });
        }
      }}
      image={getOrderImage(order)}
      menuItems={menuItems}
      amount={currencyFormatter({
        value: order.orderValue ?? 0,
        currency: order.currencyCode,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      addedToProjectValue={isOrderAddedToProjectValue(
        order.orderNo,
        opportunity
      )}
      actions={[
        {
          id: `add-to-cart-button__order-${order.orderNo}`,
          text: `${t("action.open_order_in_salja")} - ${order.orderNo}`,
          title: t("action.open_order_in_salja"),
          icon: shoppingBagIcon,
          iconOnly: true,
          disabled: openOrderInSalja.disabled,
          loading: openOrderInSalja.loading,
          onClick: openOrderInSalja.onAddToBag,
          type: "emphasised",
        },
      ]}
      details={<OrderCardDetails order={order} isWaitingList={isWaitingList} />}
    />
  );
};
